import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, FilePenLine, Trash2 } from "lucide-react";
import { type PdfExtractionDocument } from "@/redux/api/documents.rtkq";
import { formatFileSize } from "@/lib/utils";

interface ColumnActions {
  onEdit: (doc: PdfExtractionDocument) => void;
  onDelete: (doc: PdfExtractionDocument) => void;
}

export const createColumns = (actions: ColumnActions): ColumnDef<PdfExtractionDocument>[] => [
  {
    accessorKey: "storageFileName",
    header: "שם הקובץ",
    cell: ({ row }) => {
      const doc = row.original;
      return (
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-chen-900 flex-shrink-0" />
          <div className="min-w-0">
            <p
              className="font-medium text-foreground truncate cursor-pointer hover:text-chen-900 transition-colors"
              onClick={() => actions.onEdit(doc)}
            >
              {doc.storageFileName}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(doc.fileSize || 0)} • {doc.extractedContent.length} תווים
            </p>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: "תאריך העלאה",
    cell: ({ row }) => {
      const timestamp = row.original.createdAt;
      try {
        const date = new Date(timestamp as any).toLocaleDateString("he-IL", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return <span className="text-sm text-muted-foreground">{date}</span>;
      } catch {
        return <span className="text-sm text-muted-foreground">תאריך לא זמין</span>;
      }
    },
  },
  {
    accessorKey: "isScanned",
    header: "סוג מסמך",
    cell: ({ row }) => {
      const isScanned = row.original.isScanned;
      return (
        <Badge
          variant="outline"
          className={`text-xs ${
            isScanned
              ? "bg-blue-100 text-blue-700 border-blue-200"
              : "bg-slate-100 text-slate-700 border-slate-200"
          }`}
        >
          {isScanned ? "סרוק" : "רגיל"}
        </Badge>
      );
    },
  },
  {
    id: "actions",
    header: "פעולות",
    cell: ({ row }) => {
      const doc = row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit(doc)}
            title="פתח עורך"
            className="text-foreground hover:text-chen-900 hover:bg-slate-100"
          >
            <FilePenLine className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onDelete(doc)}
            title="מחק מסמך"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
];
